import { useEffect, useRef, useState } from "react";
import { saveUserSetting } from "@/shared/stores/saveUserSetting";
import { userSettingsStore } from "@/shared/stores/UserSettingsStore";

const SECTION = "bet-form";
const KEY = "global";
const DEFAULT_AMOUNT = "20";
const DEFAULT_TIME_ITEM = "";

function readStored() {
  const entry = userSettingsStore.getSetting(SECTION, KEY);
  const value = entry?.value as { amount?: unknown; timeItem?: unknown; customDurationSeconds?: unknown } | undefined;
  return {
    id: entry?.id ?? null,
    amount: typeof value?.amount === "string" ? value.amount : DEFAULT_AMOUNT,
    timeItem: typeof value?.timeItem === "string" ? value.timeItem : DEFAULT_TIME_ITEM,
    customDurationSeconds: typeof value?.customDurationSeconds === "number" ? value.customDurationSeconds : null,
  };
}

/**
 * Persists the bet form's amount and selected time period server-side via the
 * user settings store (mirrors `useIndicatorPreferences`).
 *
 * Settings are preloaded during the init splash (see `UserStore.getProfile`),
 * so on a normal page load the store is already loaded and the values hydrate
 * synchronously with no default -> saved-value flash. The async branch is a
 * fallback for the case where the store is not yet loaded at mount.
 *
 * `loaded` flips to true once hydration finishes so consumers can re-validate
 * the restored `timeItem` against the periods available for the current pair.
 */
export function useBetPreferences() {
  const initialRef = useRef<ReturnType<typeof readStored> | null>(null);
  if (!initialRef.current) initialRef.current = readStored();
  const initial = initialRef.current;

  const [betAmount, setBetAmount] = useState(initial.amount);
  const [timeItem, setTimeItem] = useState<string>(initial.timeItem);
  const [customDurationSeconds, setCustomDurationSeconds] = useState<number | null>(initial.customDurationSeconds);
  const [loaded, setLoaded] = useState(userSettingsStore.loaded);

  const settingIdRef = useRef<string | null>(initial.id);
  const loadedRef = useRef(userSettingsStore.loaded);
  const lastSavedRef = useRef(
    JSON.stringify({ amount: initial.amount, timeItem: initial.timeItem, customDurationSeconds: initial.customDurationSeconds }),
  );

  useEffect(() => {
    if (loadedRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        await userSettingsStore.load();
        if (cancelled) return;
        const stored = readStored();
        settingIdRef.current = stored.id;
        lastSavedRef.current = JSON.stringify({ amount: stored.amount, timeItem: stored.timeItem, customDurationSeconds: stored.customDurationSeconds });
        setBetAmount(stored.amount);
        setTimeItem(stored.timeItem);
        setCustomDurationSeconds(stored.customDurationSeconds);
      } catch {
        // silent
      } finally {
        if (!cancelled) {
          loadedRef.current = true;
          setLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!loadedRef.current) return;
    const serialized = JSON.stringify({ amount: betAmount, timeItem, customDurationSeconds });
    if (serialized === lastSavedRef.current) return;
    lastSavedRef.current = serialized;
    void saveUserSetting(SECTION, KEY, { amount: betAmount, timeItem, customDurationSeconds }, settingIdRef);
  }, [betAmount, timeItem, customDurationSeconds]);

  return { betAmount, setBetAmount, timeItem, setTimeItem, customDurationSeconds, setCustomDurationSeconds, loaded } as const;
}